<template>
  <div id="app">
    <div
      v-if="isImpersonating"
      class="bg-red-100 border border-red-400 px-4 py-3 mt-4 rounded text-center flex justify-center items-center"
      role="alert"
    >
      <h6 class="text-red-400 font-bold">
        You are currently impersonating a user.
      </h6>
      <button
        class="text-red-400 font-bold px-4 py-1 border rounded border-red-400 ml-4"
        @click="logout()"
      >
        Logout
      </button>
    </div>
    <nav-bar />
    <div :class="{'lg:pt-24' : !isCoursesPage}">
      <slot />
    </div>
    <nav-footer-info />
  </div>
</template>

<script>
import { mapActions, mapState, } from 'pinia';
import { useCartStore, } from '../stores/cart';
import { useSearchStore, } from '../stores/search';
import { useLoginStore, } from '../stores/login';
import NavBar from '~/components/nav/NavBar.vue';

export default {
  name: 'Default',
  components: { NavBar },
  setup() {
    const { $auth: auth } = useNuxtApp();

    return { auth };
  },
  head() {
    return {
      script: [
        {
          hid: 'trustpilot',
          src: '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
          defer: true,
          // Changed after script load
        },
      ],
    };
  },
  computed: {
    ...mapState(useLoginStore, ['isImpersonating']),
    isCoursesPage() {
      return this.$route.path.includes('courses');
    },
  },

  /*
   * Explaining the multiple app:cleared:error hooks:
   * I tried to not clear the nuxt useError() this way because it seems to call the vue hook app:cleared:error multiple times
   * likely due to the watcher being initialized multiple times. This typically happens if the watcher is being attached globally aka
   * this default.vue page and every time a layout or page is loaded, a new instance of the watcher is added.
   *
   * The only alternative I could think of is by adding to the redirects.global.js middleware but useError() and clearError() call
   * useRoute() internally which is conflicting from using the (to, from) passed into middleware.
   */
  watch: {
    // Watch the $route.path property for changes to clear useError().value so that we're not stuck on ~/error.vue
    '$route.path': {
      immediate: true,
      deep: true,
      handler() {
        if (useError().value !== null) {
          // Call clearError() whenever the URL path changes
          console.log('clearing error..');
          clearError();
        }
      },
    },
  },

  mounted() {
    this.initializeCart();
    this.initializeSearchFilters();
  },
  methods: {
    ...mapActions(useCartStore, ['initializeCart']),
    ...mapActions(useSearchStore, ['initializeSearchFilters']),
    ...mapActions(useLoginStore, ['setImpersonate']),
    async logout() {
      try {
        return await this.auth.signOut({ redirect: false }).then(() => {
          this.setImpersonate(false);
          this.$router.push('/login?logout=success');
        });
      }
      catch (error) {
        console.log('Error during sign-out:', error);
      }
    },
  },
};
</script>

<style scoped>
#app {
  font-family: Lato, sans-serif;
}
</style>
